import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import DashaTable from "./tables/DashaTable";
import DashaTitle from "./tables/DashaTitle";
import BreadcrumbItem from "./Breadcrumb";

const LEVELS = {
    MAHADASHA: 0,
    ANTARDASHA: 1,
    PRATYANTARDASHA: 2
};

export default function DashaView({ data, timezone = "UTC" }) {

    const [selectedMahadashaIndex, setSelectedMahadashaIndex] = useState(null);
    const [selectedAntardashaIndex, setSelectedAntardashaIndex] = useState(null);

    const currentLevel = useMemo(() => {
        if (selectedAntardashaIndex !== null) return LEVELS.PRATYANTARDASHA;
        if (selectedMahadashaIndex !== null) return LEVELS.ANTARDASHA;
        return LEVELS.MAHADASHA;
    }, [selectedMahadashaIndex, selectedAntardashaIndex]);


    const currentData = useMemo(() => {
        if (!data?.length) return [];

        if (selectedMahadashaIndex === null) {
            return data.map(d => d.mahadasha);
        }

        const mahadasha = data[selectedMahadashaIndex];

        if (selectedAntardashaIndex === null) {
            return mahadasha?.antardashas?.map(d => d.antardasha) || [];
        }

        return mahadasha?.antardashas[selectedAntardashaIndex]?.pratyantardashas || [];
    }, [data, selectedMahadashaIndex, selectedAntardashaIndex]);


    const activeMahadasha = useMemo(() => {
        return selectedMahadashaIndex !== null
            ? data[selectedMahadashaIndex]?.mahadasha
            : null;
    }, [data, selectedMahadashaIndex]);

    const activeAntardasha = useMemo(() => {
        return selectedMahadashaIndex !== null && selectedAntardashaIndex !== null
            ? data[selectedMahadashaIndex]?.antardashas[selectedAntardashaIndex]?.antardasha
            : null;
    }, [data, selectedMahadashaIndex, selectedAntardashaIndex]);


    useEffect(() => {
        setSelectedMahadashaIndex(null);
        setSelectedAntardashaIndex(null);
    }, [data]);


    // Navigation handlers
    const handleRowClick = useCallback((index) => {
        if (currentLevel === LEVELS.MAHADASHA) {
            setSelectedMahadashaIndex(index);
        } else if (currentLevel === LEVELS.ANTARDASHA) {
            setSelectedAntardashaIndex(index);
        }
        // No action at PRATYANTARDASHA level
    }, [currentLevel]);

    const navigateToMahadasha = useCallback(() => {
        setSelectedMahadashaIndex(null);
        setSelectedAntardashaIndex(null);
    }, []);

    const navigateToAntardasha = useCallback(() => {
        setSelectedAntardashaIndex(null);
    }, []);

    if (!data?.length)
        return <Typography>No dasha data</Typography>;

    return (
        <Box className="mt-4">
            {/* Mahadasha breadcrumb */}
            <BreadcrumbItem
                title="Mahadashas : "
                data={activeMahadasha}
                onNavigate={navigateToMahadasha}
                isClickable={activeMahadasha !== null}
            />

            {/* Antardasha breadcrumb */}
            {activeMahadasha && (
                <BreadcrumbItem
                    title="Antardashas : "
                    data={activeAntardasha}
                    onNavigate={navigateToAntardasha}
                    isClickable={activeAntardasha !== null}
                    currentLevel={1}
                />
            )}

            {/* Pratyantardasha label */}
            {activeMahadasha && activeAntardasha && (
                <div className="ml-12">
                    <DashaTitle>Pratyantardasha:</DashaTitle>
                </div>
            )}

            {/* Main table */}
            <div className={`ml-${currentLevel * 6}`}>
                <DashaTable
                    data={currentData}
                    onClick={handleRowClick}
                    className={currentLevel < LEVELS.PRATYANTARDASHA ? "hover:bg-gray-100 cursor-pointer" : ""}
                    timezone={timezone}
                />
            </div>
        </Box>
    );
}
